import { useMemo, useState } from 'react';
import {
  apiClient,
  type AllProjectsRow,
  type RoutingRule,
  type RoutingRuleAction,
  ApiError,
} from '@/lib/api/client';
import {
  COMMON_FIELDS,
  OPERATOR_LABELS,
  compileMatchJson,
  decompileMatchJson,
  type RuleCondition,
  type RuleOperator,
} from './ruleBuilder';
import { cn } from '@/lib/utils';

interface Props {
  mode: 'create' | 'edit';
  rule?: RoutingRule;
  sourceId: string;
  projects: AllProjectsRow[];
  onSaved: () => void;
  onClose: () => void;
}

const ACTIONS: Array<{ value: RoutingRuleAction; label: string; sub: string }> = [
  { value: 'route_to_project', label: 'Route to project', sub: 'Assign event to a project + optional user. Sits in project inbox until manually converted.' },
  { value: 'create_task', label: 'Auto-create task', sub: 'Skip the inbox — create a task in the target project immediately. Event body + actor land as task notes.' },
  { value: 'assign_only', label: 'Assign to user', sub: 'Set assignee without changing project.' },
  { value: 'escalate', label: 'Escalate (urgent)', sub: 'Bump severity to urgent (skips quiet hours).' },
  { value: 'silent_log', label: 'Silent log', sub: 'Mark as Seen — keep in audit, don\'t notify.' },
  { value: 'drop', label: 'Drop', sub: 'Mark Done immediately. Useful for noise filters.' },
];

export function RuleEditor({ mode, rule, sourceId, projects, onSaved, onClose }: Props) {
  const [name, setName] = useState(rule?.name ?? '');
  const [action, setAction] = useState<RoutingRuleAction>(rule?.action ?? 'route_to_project');
  const [targetProjectId, setTargetProjectId] = useState<string | null>(
    rule?.target_project_id ?? null,
  );

  // Builder state — try to round-trip rule.match_json; fall back to JSON mode if not parseable
  const initialDecompiled = useMemo(
    () => (rule ? decompileMatchJson(rule.match_json) : []),
    [rule],
  );
  const [conditions, setConditions] = useState<RuleCondition[]>(
    initialDecompiled ?? [],
  );
  const [mode_, setMode] = useState<'visual' | 'json'>(
    rule && initialDecompiled === null ? 'json' : 'visual',
  );
  const [rawJson, setRawJson] = useState(rule?.match_json ?? '');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function setCondition(i: number, patch: Partial<RuleCondition>) {
    setConditions((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function addCondition() {
    setConditions((cs) => [...cs, { field: 'type', op: 'eq', value: '' }]);
  }
  function removeCondition(i: number) {
    setConditions((cs) => cs.filter((_, idx) => idx !== i));
  }

  const compiledMatch = useMemo(() => {
    if (mode_ === 'visual') return compileMatchJson(conditions);
    return rawJson.trim() || JSON.stringify(true);
  }, [mode_, conditions, rawJson]);

  async function save() {
    if (!name.trim()) {
      setErr('Name is required');
      return;
    }
    if (mode_ === 'json') {
      try {
        JSON.parse(rawJson);
      } catch (e) {
        setErr(`Invalid JSON: ${e}`);
        return;
      }
    }
    setBusy(true);
    setErr(null);
    try {
      const body = {
        name: name.trim(),
        match_json: compiledMatch,
        action,
        target_project_id:
          action === 'route_to_project' || action === 'create_task' ? targetProjectId : null,
      };
      if (mode === 'create') {
        await apiClient.createRule(sourceId, body);
      } else if (rule) {
        await apiClient.updateRule(rule.rule_id, body);
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Rule editor"
      className="fixed inset-0 z-50 flex"
    >
      <div className="flex-1 bg-black/60" onClick={onClose} aria-hidden />
      <div className="flex w-full max-w-3xl flex-col bg-background shadow-2xl">
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {mode === 'create' ? 'New routing rule' : 'Edit routing rule'}
            </div>
            <h2 className="text-lg font-semibold">{name || 'Untitled rule'}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Esc
          </button>
        </header>
        <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6">
          {/* Name */}
          <Section label="Rule name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Escalate Stripe payment failures"
              className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </Section>
          {/* Match builder — JSON mode hidden behind <details> so non-experts
              aren't dropped into raw JSON Logic by accident. Switching back
              from JSON → visual warns if the JSON can't be decompiled (would
              silently wipe conditions). */}
          <Section
            label="Match if all of"
            aside={
              <details className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">
                  Advanced: edit JSON Logic
                </summary>
                <div className="mt-2 max-w-xs space-y-2 rounded-md border border-border bg-card/60 p-2 normal-case tracking-normal">
                  <p className="text-[11px] text-muted-foreground">
                    Editing JSON disables the visual builder until you switch back.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (mode_ === 'visual') {
                        setMode('json');
                        return;
                      }
                      // Switching back: warn if JSON isn't decompilable.
                      try {
                        const decompiled = decompileMatchJson(rawJson);
                        if (decompiled === null && rawJson.trim() !== '' && rawJson.trim() !== '{}') {
                          const ok = window.confirm(
                            'This JSON can\'t be represented in the visual builder. Switching back will wipe it. Continue?',
                          );
                          if (!ok) return;
                        }
                      } catch {
                        const ok = window.confirm(
                          'Invalid JSON. Switching back will discard it. Continue?',
                        );
                        if (!ok) return;
                      }
                      setMode('visual');
                    }}
                    className="rounded border border-border px-2 py-1 text-[10px] text-muted-foreground hover:border-ring/60 hover:text-foreground"
                  >
                    {mode_ === 'visual' ? 'Switch to JSON' : 'Switch to visual'}
                  </button>
                </div>
              </details>
            }
          >
            {mode_ === 'visual' ? (
              <div className="space-y-2">
                {conditions.length === 0 && (
                  <div className="text-xs text-muted-foreground">
                    No conditions — rule matches every event. Add at least one to narrow it.
                  </div>
                )}
                {conditions.map((c, i) => (
                  <ConditionRow
                    key={i}
                    c={c}
                    onChange={(patch) => setCondition(i, patch)}
                    onRemove={() => removeCondition(i)}
                  />
                ))}
                <button
                  onClick={addCondition}
                  className="rounded-md border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
                >
                  + Add condition
                </button>
                <details className="mt-3">
                  <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Compiled JSON Logic
                  </summary>
                  <pre className="mt-2 overflow-x-auto rounded border border-border bg-card p-2 text-[10px] text-muted-foreground">
{compiledMatch}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="space-y-1">
                <textarea
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  rows={6}
                  spellCheck={false}
                  aria-describedby="json-validity"
                  className="block w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-xs focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30"
                  placeholder='{"==": [{"var":"type"}, "feedback"]}'
                />
                {/* Live-validate footer — gives users instant feedback
                    instead of waiting for save to fail. */}
                <JsonValidity raw={rawJson} id="json-validity" />
              </div>
            )}
          </Section>
          {/* Action — role=radiogroup so SR announces "1 of N" and arrow keys
              move selection naturally. Native radios would carry visual
              chrome; faking the role lets us keep the card visuals while
              fixing the a11y "5 buttons" announcement. */}
          <Section label="Action">
            <div role="radiogroup" aria-label="Rule action" className="grid gap-2 md:grid-cols-2">
              {ACTIONS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  role="radio"
                  aria-checked={action === a.value}
                  onClick={() => setAction(a.value)}
                  className={cn(
                    'rounded-md border px-3 py-2.5 text-left transition-colors',
                    action === a.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-ring/60',
                  )}
                >
                  <div className="text-sm font-semibold text-foreground">{a.label}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">{a.sub}</div>
                </button>
              ))}
            </div>
            {(action === 'route_to_project' || action === 'create_task') && (
              <div className="mt-3 space-y-1">
                <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {action === 'create_task' ? 'Create task in project' : 'Route to project'}
                </label>
                <select
                  value={targetProjectId ?? ''}
                  onChange={(e) => setTargetProjectId(e.target.value || null)}
                  className="block w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
                >
                  <option value="">— pick a project —</option>
                  {projects.map((p) => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.name} ({p.home_ws_name})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </Section>
          {err && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
              {err}
            </div>
          )}
        </div>
        <footer className="flex gap-2 border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            className="flex-1 rounded-md border border-border bg-background px-4 py-2 text-sm text-muted-foreground hover:border-ring/60 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !name.trim()}
            className="flex-[2] rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Create rule' : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ConditionRow({
  c,
  onChange,
  onRemove,
}: {
  c: RuleCondition;
  onChange: (patch: Partial<RuleCondition>) => void;
  onRemove: () => void;
}) {
  const needsValue = c.op !== 'exists' && c.op !== 'not_exists';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2">
      <select
        value={c.field}
        onChange={(e) => onChange({ field: e.target.value })}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
      >
        {COMMON_FIELDS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
        {!COMMON_FIELDS.includes(c.field as (typeof COMMON_FIELDS)[number]) && (
          <option value={c.field}>{c.field} (custom)</option>
        )}
      </select>
      <select
        value={c.op}
        onChange={(e) => onChange({ op: e.target.value as RuleOperator })}
        className="rounded-md border border-border bg-background px-2 py-1.5 text-xs"
      >
        {Object.entries(OPERATOR_LABELS).map(([v, label]) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
      {needsValue && (
        <input
          value={c.value}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="value"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs"
        />
      )}
      <button
        onClick={onRemove}
        className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive"
        aria-label="Remove condition"
      >
        ×
      </button>
    </div>
  );
}

function Section({
  label,
  children,
  aside,
}: {
  label: string;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </label>
        {aside}
      </div>
      {children}
    </div>
  );
}

/** Live JSON validator — green ✓ if parses + non-empty, otherwise red ✗
 *  with the parse-error message. Empty raw counts as valid (matches every
 *  event, per the visual builder's behavior with zero conditions). */
function JsonValidity({ raw, id }: { raw: string; id: string }) {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '{}') {
    return (
      <p id={id} className="font-mono text-[10px] text-muted-foreground/70">
        Empty rule — matches every event.
      </p>
    );
  }
  try {
    JSON.parse(trimmed);
    return (
      <p id={id} className="font-mono text-[10px] text-emerald-400">
        ✓ Valid JSON
      </p>
    );
  } catch (e) {
    return (
      <p id={id} role="status" className="font-mono text-[10px] text-destructive">
        ✗ {e instanceof Error ? e.message : 'Invalid JSON'}
      </p>
    );
  }
}
